import { useCallback, useEffect, useRef, useState } from "react"

// Whether the member wants questions read to them. Off by default on desktop
// (plan Q14) and remembered per browser, so a member who turns it on once does
// not have to turn it on again when they resume.
const PREFERENCE_KEY = "kf:member-story:read-aloud"

function readCsrfToken() {
  if (typeof document === "undefined") return null
  return document.querySelector('meta[name="csrf-token"]')?.content || null
}

function browserSpeak(text) {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return false
  try {
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = 0.98
    utterance.lang = "en-US"
    window.speechSynthesis.speak(utterance)
    return true
  } catch {
    return false
  }
}

/**
 * Reads the bot's questions aloud.
 *
 * The seven fixed questions are pre-rendered on the server and served from a
 * cached URL; a generated follow-up is rendered on demand. Either request can
 * fail — the model is a third party — so both fall back to the browser's own
 * speechSynthesis rather than leaving a member who is listening in silence.
 *
 *   const { enabled, supported, toggle, speak } = useReadAloud({ token })
 *   speak({ text: turn.text, position: turn.isFollowup ? null : turn.position })
 */
export function useReadAloud({ token, defaultEnabled = false }) {
  const [enabled, setEnabled] = useState(defaultEnabled)
  // Mirrors `enabled` so `toggle` can read it without re-creating itself.
  const enabledRef = useRef(defaultEnabled)
  const audioRef = useRef(null)

  // Read the stored preference after mount: this page is client-rendered, but
  // reading localStorage during render would still be a side effect in the
  // render path.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(PREFERENCE_KEY)
      if (stored !== null) {
        enabledRef.current = stored === "true"
        setEnabled(enabledRef.current)
      }
    } catch {
      // Private browsing or storage disabled — the default stands.
    }
  }, [])

  const supported =
    typeof window !== "undefined" &&
    (typeof window.Audio !== "undefined" || "speechSynthesis" in window)

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel()
      } catch {
        // Nothing was speaking.
      }
    }
  }, [])

  // The side effects live OUT here, not inside the setState updater. React may
  // call an updater more than once for the same click, which would write the
  // preference twice and — worse — leave the stored value disagreeing with what
  // is on screen. `enabledRef` is what lets this read the current value without
  // taking `enabled` as a dependency and rebuilding the callback every render.
  const toggle = useCallback(() => {
    const next = !enabledRef.current
    enabledRef.current = next
    setEnabled(next)

    try {
      window.localStorage.setItem(PREFERENCE_KEY, String(next))
    } catch {
      // Preference simply won't persist.
    }
    if (!next) stop()
  }, [stop])

  const play = useCallback(
    async (src) => {
      stop()
      const audio = new Audio(src)
      audioRef.current = audio
      await audio.play()
    },
    [stop]
  )

  // `position` names one of the seven fixed questions (cached audio); omit it
  // for a generated follow-up, which is rendered on demand.
  const speak = useCallback(
    async ({ text, position = null }) => {
      if (!enabled || !text) return

      try {
        if (position) {
          await play(
            `/app/member/member_story_interviews/${token}/question_audio?position=${position}`
          )
          return
        }

        const response = await fetch(
          `/app/member/member_story_interviews/${token}/speech`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": readCsrfToken() || "",
              Accept: "application/json",
            },
            credentials: "same-origin",
            body: JSON.stringify({ text }),
          }
        )
        if (!response.ok) throw new Error("speech unavailable")

        const data = await response.json()
        await play(`data:audio/mpeg;base64,${data.audio}`)
      } catch {
        // Read-aloud is a nice-to-have: fall back to the browser's own voice
        // rather than letting a listening member sit in silence.
        browserSpeak(text)
      }
    },
    [enabled, play, token]
  )

  useEffect(() => stop, [stop])

  return { enabled, supported, toggle, speak, stop }
}
